import React, { useCallback, useEffect, useState } from "react";
import LeftMenu from "../components/StudentLeftside";
import Select from "react-select";
import Swal from "sweetalert2";
import "./ProfilePage.css";
import API_BASE_URL from "../config/api";

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [imgUploading, setImgUploading] = useState(false);
  const [error, setError] = useState(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const [userId, setUserId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email_id: "",
    mobile_no: "",
    dob: "",
    address: "",
    state_id: "",
    city_id: "",
    pincode: "",
    profile_pic: "",
  });

  const STATES_ENDPOINT = `${API_BASE_URL}/get_states.php`;
  const CITIES_ENDPOINT = `${API_BASE_URL}/get_cities.php`;
  const PROFILE_ENDPOINT = `${API_BASE_URL}/user_profile_update.php`;
const DEFAULT_AVATAR = `${API_BASE_URL}/assets/img/profile.png`;
  // react-select styling
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "44px",
      height: "44px",
      borderRadius: "8px",
      border: state.isFocused ? "1.5px solid #164d85" : "1px solid #ccc",
      boxShadow: "none",
      fontSize: "15px",
      background: "#fff",
    }),
    valueContainer: (provided) => ({ ...provided, height: "44px", padding: "0 8px" }),
    singleValue: (provided) => ({ ...provided, color: "#111" }),
    placeholder: (provided) => ({ ...provided, color: "#666" }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  // Broadcast helper so LeftMenu + HeaderPath update instantly
  const broadcastUserUpdate = (newUser) => {
    try {
      sessionStorage.setItem("user", JSON.stringify(newUser));
    } catch {}
    window.dispatchEvent(new CustomEvent("userUpdated", { detail: newUser }));
  };

  // Load logged-in userId
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("user");
      const u = raw ? JSON.parse(raw) : null;
      if (u?.id || u?.userid || u?.userId) {
        setUserId(u.id ?? u.userid ?? u.userId);
      }
    } catch {}
    setLoading(false);
  }, []);

  // Fetch states
  const fetchStates = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append("load", "1");

      const response = await fetch(STATES_ENDPOINT, { method: "POST", body: fd });
      const raw = await response.text();
      const data = JSON.parse(raw);

      const arr = Array.isArray(data) ? data : data.states ?? [];
      const formatted = arr.map((s) => ({
        value: String(s.state_id ?? s.id),
        label: s.state_title ?? s.name,
      }));

      setStateOptions(formatted);
    } catch (err) {
      Swal.fire("Error", "Failed to load states", "error");
    }
  }, [STATES_ENDPOINT]);

  // Fetch cities based on selected state
  const fetchCities = useCallback(async (state_id) => {
    setCitiesLoading(true);
    try {
      const fd = new FormData();
      fd.append("state_id", state_id);

      const response = await fetch(CITIES_ENDPOINT, { method: "POST", body: fd });
      const raw = await response.text();
      const data = JSON.parse(raw);

      const arr = Array.isArray(data) ? data : data.cities ?? [];
      const formatted = arr.map((c) => ({
        value: String(c.city_id ?? c.id),
        label: c.city_title ?? c.city ?? c.city_name,
      }));

      setCityOptions(formatted);
    } catch {
      Swal.fire("Error", "Failed to load cities", "error");
    } finally {
      setCitiesLoading(false);
    }
  }, [CITIES_ENDPOINT]);

  // Fetch profile
  useEffect(() => {
    if (!userId) return;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append("userid", userId);

        const response = await fetch(PROFILE_ENDPOINT, { method: "POST", body: fd });
        const raw = await response.text();
        const data = JSON.parse(raw);

        if (!data.success) throw new Error(data.message);

        const u = data.userdata;

        setFormData({
          name: u.name,
          email_id: u.email_id,
          mobile_no: u.mobile_no,
          dob: u.dob ?? "",
          address: u.address ?? "",
          state_id: String(u.state_id ?? ""),
          city_id: String(u.city_id ?? ""),
          pincode: u.pincode ?? "",
          profile_pic: u.profile_pic
            ? `${API_BASE_URL}/${u.profile_pic}`
            : `${process.env.PUBLIC_URL}/assets/img/profile.png`,
        });

        await fetchStates();
        if (u.state_id) await fetchCities(String(u.state_id));
      } catch (err) {
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, PROFILE_ENDPOINT, fetchStates, fetchCities]);

  // Upload Profile Picture
  const uploadProfilePic = async (file) => {
    if (!file) return;
    setImgUploading(true);

    try {
      const fd = new FormData();
      fd.append("userid", userId);
      fd.append("profile_pic", file);

      const response = await fetch(PROFILE_ENDPOINT, { method: "POST", body: fd });
      const raw = await response.text();
      const data = JSON.parse(raw);

      if (!data.success) throw new Error(data.message);

      const u = data.userdata;

      setFormData((prev) => ({
        ...prev,
        profile_pic: `${API_BASE_URL}/${u.profile_pic}`,
      }));

      broadcastUserUpdate({
        id: userId,
        name: u.name,
        profile_pic: u.profile_pic,
        picture_url: `${API_BASE_URL}/${u.profile_pic}`,
        city: u.city_title ?? u.city,
      });

      Swal.fire("Success", "Profile picture updated", "success");

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setImgUploading(false);
    }
  };

  // Handle inputs (including image upload)
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "profile_pic" && files?.length) {
      const file = files[0];

      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("Too Large", "Max 5MB allowed", "warning");
        return;
      }

      setPreviewUrl(URL.createObjectURL(file));
      uploadProfilePic(file);
      return;
    }

    if (name === "state_id") {
      setFormData((prev) => ({ ...prev, state_id: value, city_id: "" }));
      fetchCities(value);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Validation + Save Profile
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) return Swal.fire("Required", "Enter Name", "warning");
    if (!formData.dob) return Swal.fire("Required", "Select Date of Birth", "warning");
    if (!formData.address.trim()) return Swal.fire("Required", "Enter Address", "warning");
    if (!formData.state_id) return Swal.fire("Required", "Select State", "warning");
    if (!formData.city_id) return Swal.fire("Required", "Select City", "warning");

    if (!/^\d{6}$/.test(formData.pincode))
      return Swal.fire("Invalid", "PIN Code must be 6 digits", "warning");

    setLoading(true);
    try {
      const fd = new FormData();
      ["name", "dob", "address", "state_id", "city_id", "pincode"].forEach((f) =>
        fd.append(f, formData[f])
      );
      fd.append("userid", userId);
      fd.append("email_id", formData.email_id);
      fd.append("mobile_no", formData.mobile_no);

      const response = await fetch(PROFILE_ENDPOINT, { method: "POST", body: fd });
      const raw = await response.text();
      const data = JSON.parse(raw);

      if (!data.success) throw new Error(data.message);

      const u = data.userdata;

      // update local data
      setFormData((prev) => ({
        ...prev,
        name: u.name,
        dob: u.dob,
        address: u.address,
        state_id: String(u.state_id),
        city_id: String(u.city_id),
        pincode: u.pincode,
      }));

      // broadcast to LeftMenu + Header
      broadcastUserUpdate({
        id: userId,
        name: u.name,
        picture_url: u.profile_pic ? `${API_BASE_URL}/${u.profile_pic}` : undefined,
        city: u.city_title ?? u.city,
      });

      Swal.fire("Success", "Profile updated successfully", "success");
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Selection objects
  const selectedState = stateOptions.find((s) => s.value === formData.state_id) || null;
  const selectedCity = cityOptions.find((c) => c.value === formData.city_id) || null;

  const avatarSrc = previewUrl || formData.profile_pic || DEFAULT_AVATAR;

  if (loading) return <div className="p-3">Loading…</div>;
  if (error) return <div className="text-danger">{error}</div>;

  return (
    <div className="dashboard-wrap bg-light">
      <LeftMenu />

      <div className="dashboard-content p-4">
        <div className="profile-header">
          <h3 className="profile-title">Edit Profile</h3>

          <div className="profile-picture-wrapper">
            <div className="avatar-wrapper">
              <img src={avatarSrc} alt="Profile" className="profile-avatar-header" />

              {imgUploading && (
                <div className="avatar-spinner">
                  <div className="spinner-border spinner-border-sm"></div>
                </div>
              )}
            </div>

            <label className="btn btn-outline-primary btn-sm mt-2">
              Change Photo
              <input type="file" hidden name="profile_pic" accept="image/*" onChange={handleChange} />
            </label>
          </div>
        </div>

        <div className="profile-form">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">

              <div className="col-md-6">
                <label>Name</label>
                <input className="form-control important" name="name" value={formData.name} onChange={handleChange} />
              </div>

              <div className="col-md-6">
                <label>Email</label>
                <input className="form-control non-editable" value={formData.email_id} readOnly />
              </div>

              <div className="col-md-6">
                <label>Phone</label>
                <input className="form-control non-editable" value={formData.mobile_no} readOnly />
              </div>

              <div className="col-md-6">
                <label>DOB</label>
                <input type="date" className="form-control important" name="dob" value={formData.dob} onChange={handleChange} />
              </div>

              <div className="col-md-12">
                <label>Address</label>
                <input className="form-control important" name="address" value={formData.address} onChange={handleChange} />
              </div>

              <div className="col-md-6">
                <label>State</label>
                <Select
                  options={stateOptions}
                  value={selectedState}
                  onChange={(s) => handleChange({ target: { name: "state_id", value: s ? s.value : "" } })}
                  styles={customStyles}
                  isSearchable
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="col-md-6">
                <label>City</label>
                <Select
                  options={cityOptions}
                  value={selectedCity}
                  onChange={(c) => handleChange({ target: { name: "city_id", value: c ? c.value : "" } })}
                  styles={customStyles}
                  isSearchable
                  isLoading={citiesLoading}
                  menuPortalTarget={document.body}
                />
              </div>

              <div className="col-md-6">
                <label>Pin Code</label>
                <input className="form-control important" name="pincode" value={formData.pincode} onChange={handleChange} />
              </div>

            </div>

            <div className="text-end mt-4">
              <button className="btn btn-warning text-white" disabled={loading || imgUploading}>
                {loading ? "Saving…" : "Save Profile"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
